import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, MenuItem, Paper,
    Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Typography,
} from '@mui/material';
import { StockReservationService } from '../../services/stockReservation.service';
import { BuyerOrderService } from '../../services/buyerOrder.service';
import { ItemService } from '../../services/item.service';
import { getStoresByInstitutionId } from '../../services/store.service';
import { AuthService } from '../../services/auth.service';
import { ReservationStatus, StockReservation } from '../../interfaces/stockReservation.interface';
import { IBuyerOrder } from '../../interfaces/garments/buyerOrder.interface';
import { Item } from '../../interfaces/item.interface';
import { Store } from '../../interfaces/store.interface';
import SearchableSelect from '../common/SearchableSelect';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';

const STATUS_LABEL: Record<ReservationStatus, string> = { ACTIVE: 'Active', RELEASED: 'Released', CONSUMED: 'Used' };
const STATUS_COLOR: Record<ReservationStatus, 'warning' | 'success' | 'default'> = {
    ACTIVE: 'warning', CONSUMED: 'success', RELEASED: 'default',
};

/** Stock held for buyer orders, for the store people: who it is held for, where, how much is still held. */
const StockReservationList: React.FC = () => {
    const toast = useToast();
    const canManage = usePermission(PERMISSIONS.STOCK_RESERVATION_MANAGE);
    const [rows, setRows] = useState<StockReservation[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [total, setTotal] = useState(0);

    const [status, setStatus] = useState<ReservationStatus | ''>('ACTIVE');
    const [buyerOrderId, setBuyerOrderId] = useState('');
    const [orderNumber, setOrderNumber] = useState('');
    const [ordersUnavailable, setOrdersUnavailable] = useState(false);
    const [itemId, setItemId] = useState('');
    const [storeId, setStoreId] = useState('');

    const [orders, setOrders] = useState<IBuyerOrder[]>([]);
    const [items, setItems] = useState<Item[]>([]);
    const [stores, setStores] = useState<Store[]>([]);
    const [itemSearch, setItemSearch] = useState('');
    const [selectedItem, setSelectedItem] = useState<Item | null>(null);

    const [releasing, setReleasing] = useState<StockReservation | null>(null);
    const [saving, setSaving] = useState(false);
    const loadSeq = useRef(0);

    useEffect(() => {
        const institutionId = AuthService.getSelectedInstitution();
        if (institutionId) {
            BuyerOrderService.list(institutionId, 0, 100)
                .then(r => { setOrders(r.content ?? []); setOrdersUnavailable(false); })
                // a store role may not read buyer orders: fall back to typing the order number
                .catch(() => { setOrders([]); setOrdersUnavailable(true); });
        }
        getStoresByInstitutionId(0, 100, 'ALL').then(r => setStores(r.content)).catch(() => setStores([]));
    }, []);

    useEffect(() => {
        let cancelled = false;
        const handle = setTimeout(() => {
            ItemService.getItemsByInstitution(0, 30, itemSearch, 'STOCK')
                .then(r => { if (!cancelled) setItems(r.content); })
                .catch(() => { if (!cancelled) setItems([]); });
        }, 250);
        return () => { cancelled = true; clearTimeout(handle); };
    }, [itemSearch]);

    const load = useCallback(async () => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId) return;
        const seq = ++loadSeq.current;   // a slower, older response must not overwrite a newer one
        try {
            setLoading(true);
            const r = await StockReservationService.list(institutionId, { status, buyerOrderId, itemId, storeId, orderNumber: orderNumber.trim() }, page, rowsPerPage);
            if (seq !== loadSeq.current) return;
            setRows(r.content);
            setTotal(r.totalElements);
        } catch (err) {
            if (seq !== loadSeq.current) return;
            toast.showApiError(err, 'Failed to load stock reservations');
        } finally {
            if (seq === loadSeq.current) setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status, buyerOrderId, orderNumber, itemId, storeId, page, rowsPerPage]);

    useEffect(() => { load(); }, [load]);

    const release = async () => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!releasing || !institutionId) return;
        try {
            setSaving(true);
            await StockReservationService.release(institutionId, releasing.id);
            toast.showSuccess('Reservation released');
            setReleasing(null);
            await load();
        } catch (err) {
            toast.showApiError(err, 'Failed to release the reservation');
        } finally {
            setSaving(false);
        }
    };

    const resetPage = () => setPage(0);

    return (
        <Box sx={{ p: 3 }} data-help-page="store.reservations.page">
            <Typography variant="h5" sx={{ mb: 2 }}>Stock reservations</Typography>

            <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }} data-help-section="store.reservations.filter">
                <TextField select size="small" label="Status" data-help="store.reservations.filter.status"
                    sx={{ flex: '1 1 140px', maxWidth: 200 }} value={status}
                    onChange={(e) => { setStatus(e.target.value as ReservationStatus | ''); resetPage(); }}
                    data-testid="reservation-status-filter">
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="ACTIVE">Active</MenuItem>
                    <MenuItem value="CONSUMED">Used</MenuItem>
                    <MenuItem value="RELEASED">Released</MenuItem>
                </TextField>
                {ordersUnavailable ? (
                    <TextField size="small" label="Order number" value={orderNumber}
                        onChange={(e) => { setOrderNumber(e.target.value); resetPage(); }}
                        sx={{ flex: '1 1 220px', maxWidth: 320 }} data-help="store.reservations.filter.order"
                        slotProps={{ htmlInput: { 'data-testid': 'reservation-order-text-filter' } }} />
                ) : (
                    <SearchableSelect<IBuyerOrder>
                        options={orders}
                        value={buyerOrderId}
                        onChange={(id) => { setBuyerOrderId(id); resetPage(); }}
                        getOptionLabel={(o) => o.orderNumber}
                        data-help="store.reservations.filter.order" label="Buyer order"
                        clearable
                        fullWidth={false}
                        sx={{ flex: '1 1 220px', maxWidth: 320 }}
                        data-testid="reservation-order-filter"
                    />
                )}
                <SearchableSelect<Item>
                    options={items}
                    value={itemId}
                    selectedOption={selectedItem}
                    disableClientFilter
                    onInputChange={setItemSearch}
                    onChange={(id, opt) => { setItemId(id); setSelectedItem(opt); resetPage(); }}
                    getOptionLabel={(i) => `${i.itemName} (${i.itemCode})`}
                    data-help="store.reservations.filter.item" label="Item"
                    clearable
                    fullWidth={false}
                    sx={{ flex: '1 1 220px', maxWidth: 320 }}
                    data-testid="reservation-item-filter"
                />
                <SearchableSelect<Store>
                    options={stores}
                    value={storeId}
                    onChange={(id) => { setStoreId(id); resetPage(); }}
                    getOptionLabel={(s) => s.storeName}
                    data-help="store.reservations.filter.store" label="Store"
                    clearable
                    fullWidth={false}
                    sx={{ flex: '1 1 220px', maxWidth: 320 }}
                    data-testid="reservation-store-filter"
                />
            </Box>

            <TableContainer component={Paper} data-help-section="store.reservations.table">
                <Table size="small" sx={{ minWidth: 720 }} data-testid="reservation-table">
                    <TableHead>
                        <TableRow sx={{ backgroundColor: '#f5f5f5', '& th': { fontWeight: 'bold' } }}>
                            <TableCell data-help="store.reservations.col.order">Buyer order</TableCell>
                            <TableCell data-help="store.reservations.col.item">Item</TableCell>
                            <TableCell data-help="store.reservations.col.store">Store</TableCell>
                            <TableCell data-help="store.reservations.col.reserved" align="right">Reserved</TableCell>
                            <TableCell data-help="store.reservations.col.used" align="right">Used</TableCell>
                            <TableCell data-help="store.reservations.col.held" align="right">Still held</TableCell>
                            <TableCell data-help="store.reservations.col.status">Status</TableCell>
                            <TableCell data-help="store.reservations.col.actions">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading && (
                            <TableRow><TableCell colSpan={8} align="center" data-testid="reservation-loading">Loading...</TableCell></TableRow>
                        )}
                        {!loading && rows.length === 0 && (
                            <TableRow><TableCell colSpan={8} align="center">No stock is reserved.</TableCell></TableRow>
                        )}
                        {!loading && rows.map(r => (
                            <TableRow key={r.id} hover data-testid={`reservation-row-${r.id}`}>
                                <TableCell>{r.orderNumber ?? '-'}</TableCell>
                                <TableCell>
                                    {r.itemName}
                                    {r.itemCode && <Typography component="div" variant="caption" color="text.secondary">{r.itemCode}</Typography>}
                                </TableCell>
                                <TableCell>{r.storeName}</TableCell>
                                <TableCell align="right">{Number(r.reservedQty)}{r.unit ? ` ${r.unit}` : ''}</TableCell>
                                <TableCell align="right">{Number(r.consumedQty)}</TableCell>
                                <TableCell align="right">{Number(r.openQty)}</TableCell>
                                <TableCell><Chip size="small" color={STATUS_COLOR[r.status]} label={STATUS_LABEL[r.status]} /></TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                    {canManage && r.status === 'ACTIVE' && (
                                        <Button size="small" color="warning" data-help="store.reservations.action.release"
                                            onClick={() => setReleasing(r)}>Release</Button>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={total}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    rowsPerPageOptions={[10, 25, 50]}
                    onRowsPerPageChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
                />
            </TableContainer>

            <Dialog open={!!releasing} onClose={() => !saving && setReleasing(null)} fullWidth maxWidth="xs">
                <DialogTitle>Release reservation</DialogTitle>
                <DialogContent>
                    {releasing && (
                        <DialogContentText>
                            Release {Number(releasing.openQty)} of {releasing.itemName} held in {releasing.storeName} for order{' '}
                            {releasing.orderNumber}? It becomes free stock again.
                        </DialogContentText>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setReleasing(null)} disabled={saving}>Cancel</Button>
                    <Button variant="contained" color="warning" onClick={release} disabled={saving} data-testid="reservation-release-confirm">
                        Release
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default StockReservationList;
